import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [playlistId, setPlaylistId] = useState(null);
  const [currentSongIndex, setCurrentSongIndex] = useState(-1);
  const audioRef = useRef(null);
  const previousVolumeRef = useRef(1);

  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;

    const handleEnded = () => {
      const nextIndex = currentSongIndex + 1;
      if (playlist[nextIndex]?.audioUrl) {
        playNextSong();
      } else {
        setIsPlaying(false);
      }
    };

    const handleCanPlay = () => {
      if (isPlaying && audio.paused) {
        audio.play().catch(console.error);
      }
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [currentSong, isPlaying, currentSongIndex, playlist]);

  const playSong = (song) => {
    if (audioRef.current && song?.audioUrl) {
      setIsPlaying(true);
      audioRef.current.src = song.audioUrl;
      audioRef.current.play().catch(console.error);
    }
  };

  const playNextSong = () => {
    if (playlist.length > 0 && currentSongIndex < playlist.length - 1) {
      const nextIndex = currentSongIndex + 1;
      if(playlist[nextIndex] && playlist[nextIndex].audioUrl) {
        console.log('Playing next song at index', nextIndex);
        setCurrentSongIndex(nextIndex);
        setCurrentSong(playlist[nextIndex]);
        playSong(playlist[nextIndex]);
      } else {
        console.log('No next song to play');
        if (audioRef.current && audioRef.current.duration < Infinity) {
          // Directly set the currentTime to duration for an abrupt transition
          audioRef.current.currentTime = audioRef.current.duration;
          setCurrentTime(audioRef.current.duration);
        } else {
          setIsPlaying(false);
        }
      }
    } else {
      if (audioRef.current?.duration < Infinity) {
        audioRef.current.currentTime = audioRef.current.duration;
        setCurrentTime(audioRef.current.duration);
      } else {
        setIsPlaying(false);
      }
    }
  };

  const playPreviousSong = () => {
    if (playlist.length > 0 && currentSongIndex > 0) {
      const prevIndex = currentSongIndex - 1;
      const prevSong = playlist[prevIndex];
      setCurrentSongIndex(prevIndex);
      setCurrentSong(prevSong);
      playSong(prevSong);
    } else {
      handleSeek(0);
    }
  };

  const handleSeek = (newTime) => {
    if (audioRef.current?.duration < Infinity) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (newVolume) => {
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setVolume(newVolume);
      if (newVolume > 0) setIsMuted(false);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = previousVolumeRef.current;
        setVolume(previousVolumeRef.current);
      } else {
        previousVolumeRef.current = volume;
        audioRef.current.volume = 0;
        setVolume(0);
      }
      setIsMuted(!isMuted);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        if (audioRef.current.currentTime >= audioRef.current.duration) {
          handleSeek(0);
        }
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (audio?.duration && !isNaN(audio.duration) && audio.duration > 0) {
      setDuration(audio.duration);
    } else {
      setDuration(-1);
    }
  };

  const setPlaylistAndCurrentSong = (newPlaylist, song, id = null) => {
    const index = newPlaylist.findIndex(s => s._id === song._id);
    let currentPlaylistId = playlistId
    setPlaylist(newPlaylist);
    setPlaylistId(id);
    if(currentSongIndex !== index || currentPlaylistId !== id) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setCurrentSongIndex(index);
      setCurrentSong(song);
      playSong(song);
    }
  };

  const value = {
    currentSong,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playlist,
    playlistId,
    currentSongIndex,
    audioRef,
    playSong,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleSeek,
    handleVolumeChange,
    handlePlayPause,
    toggleMute,
    setCurrentSong,
    setIsPlaying,
    setPlaylistAndCurrentSong,
    playNextSong,
    playPreviousSong
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        style={{ display: 'none' }}
      />
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};
